import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import logger from 'Utils/Logger';
import StartGame from './main';
import { reconfigureScale } from './reconfigureScale';
import {
  ClearQueuePhaser,
  ClearQueueReact,
  EventBus,
  PhaserToReactListeners,
  NotifyPhaser,
  ReactListenForPhaser,
  NotifyPhaserEvents,
  RemoveListener,
} from 'Phaser/EventBus2';

export interface IRefPhaserGame {
  game: Phaser.Game | null;
  scene: Phaser.Scene | null;
  /** True when the game was reused from a previous mount (singleton). */
  wasReused: boolean;
}

interface IProps {
  currentActiveScene?: (scene_instance: Phaser.Scene) => void;
  width: number;
  height: number;
  scaling?: number;
  crisp?: boolean;
  playButtonOverlap?: number;
}

/**
 * Module-level singleton state for the Phaser game instance.
 *
 * On iOS WKWebView, creating a new WebGL context after destroying one crashes
 * the process. By keeping a single game alive for the lifetime of the app we
 * avoid ever needing to create a second WebGL context.
 *
 * On unmount we stop all running scenes, clear EventBus listeners and queues,
 * sleep the game loop, and detach the canvas from the DOM. On remount we
 * reattach the canvas, wake the game loop, and let the container start a
 * fresh scene.
 */
const singleton = {
  game: null as Phaser.Game | null,
  canvas: null as HTMLCanvasElement | null,
  /**
   * Monotonically increasing counter incremented each time a PhaserGame mounts.
   * Used to detect stale unmount cleanups: when React remounts with a new key,
   * the old component's useEffect cleanup runs AFTER the new component's
   * useLayoutEffect mount.  Without this guard the old cleanup would undo the
   * new mount's wake/reattach, leaving the game asleep with a detached canvas.
   */
  mountGeneration: 0,
  /**
   * Set to false after each useLayoutEffect mount, set to true when the
   * useEffect unmount cleanup actually runs (i.e. is not skipped by the
   * mount-generation guard).  When getOrCreateSingletonGame sees this
   * as false, it knows the previous cleanup was skipped and stale
   * EventBus listeners need to be cleared.
   */
  cleanupRan: true,
  /** Cleanup function from reconfigureScale — disconnects ResizeObserver. */
  scaleCleanup: (() => {}) as () => void,
};

/**
 * Resume the Phaser game's WebAudio context if it was suspended.
 * Browsers suspend the AudioContext when the tab is hidden or inactive.
 * Eagerly resuming it avoids a pop/distortion on the first sound after wake.
 */
function resumePhaserAudioContext(game: Phaser.Game): void {
  try {
    const ctx = (game.sound as Phaser.Sound.WebAudioSoundManager)?.context;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume();
    }
  } catch {
    // Not a WebAudio sound manager or context unavailable — safe to ignore
  }
}

/**
 * Fully destroy a Phaser game and release its WebGL resources.
 * Only used for context-loss recovery — not for normal unmount.
 */
function destroyGame(game: Phaser.Game): void {
  const canvas = game.canvas;
  const renderer = game.renderer;

  game.destroy(true);

  if (canvas) {
    canvas.width = 0;
    canvas.height = 0;
  }

  if (renderer && 'gl' in renderer && renderer.gl) {
    const loseCtx = renderer.gl.getExtension('WEBGL_lose_context');
    if (loseCtx) {
      loseCtx.loseContext();
    }
  }
}

function destroySingletonGame(): void {
  if (!singleton.game) return;
  destroyGame(singleton.game);
  singleton.game = null;
  singleton.canvas = null;
}

// ---------------------------------------------------------------------------
// Singleton helpers
// ---------------------------------------------------------------------------

function getOrCreateSingletonGame(
  parentId: string,
  width: number,
  height: number,
  scaling?: number,
  crisp?: boolean,
): { game: Phaser.Game; wasReused: boolean } {
  if (singleton.game) {
    // Clean up stale EventBus state ONLY when the previous consumer's
    // cleanup was skipped.  During route changes the new component's
    // useLayoutEffect runs BEFORE the old component's useEffect cleanup,
    // and the mount-generation guard causes that cleanup to be skipped.
    // This leaves stale listeners (e.g. ResizeWindow) that can corrupt
    // scale state.  However, during same-route remounts (e.g. tier
    // switch), the old cleanup runs FIRST and already clears everything.
    // Clearing again would destroy queued events (like AddPhraseLegacy)
    // that parent components sent before the new scene starts.
    //
    // We use singleton.cleanupRan (not EventBus.eventNames().length)
    // because parent components (MainAppPhaser) register listeners
    // (e.g. SetPhrasesTillNextTier) that survive PhaserGame unmount —
    // checking for any listeners would incorrectly trigger clearing
    // during tier switches.
    if (!singleton.cleanupRan) {
      EventBus.removeAllListeners();
      ClearQueueReact();
      ClearQueuePhaser();
    }

    const container = document.getElementById(parentId);
    if (container && singleton.canvas && !container.contains(singleton.canvas)) {
      container.appendChild(singleton.canvas);
    }

    // Check WebGL context state before wake
    const gl = (singleton.game.renderer as Phaser.Renderer.WebGL.WebGLRenderer)?.gl;
    const ctxLost = typeof gl?.isContextLost === 'function' ? gl.isContextLost() : 'n/a';
    singleton.game.loop?.wake?.();

    // Reconfigure scale manager for the new consumer's dimensions/mode.
    // Disconnect the previous observer (if any) before creating a new one.
    singleton.scaleCleanup();
    if (container && singleton.canvas) {
      singleton.scaleCleanup = reconfigureScale(
        singleton.game,
        singleton.canvas,
        container,
        width,
        height,
        scaling,
        crisp,
      );
    }
    resumePhaserAudioContext(singleton.game);

    return { game: singleton.game, wasReused: true };
  }

  singleton.game = StartGame(parentId, width, height, scaling, crisp);
  singleton.canvas = singleton.game.canvas;
  return { game: singleton.game, wasReused: false };
}

/**
 * Stop all running/active scenes. This triggers each scene's `shutdown` event,
 * which in turn calls the scene's `destroy()` / `unload()` method to clean up
 * game objects, event listeners, and internal state.
 */
function stopAllScenes(game: Phaser.Game): void {
  game.scene.scenes.forEach((scene: Phaser.Scene) => {
    if (scene.sys?.isActive() || scene.sys?.isPaused()) {
      game.scene.stop(scene.scene.key);
    }
  });
}

function sleepSingletonGame(): void {
  if (singleton.game) {
    singleton.game.loop?.sleep?.();
  }
}

function detachCanvas(): void {
  if (singleton.canvas && singleton.canvas.parentElement) {
    singleton.canvas.parentElement.removeChild(singleton.canvas);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PhaserGame = forwardRef<IRefPhaserGame, IProps>(function PhaserGame(
  { currentActiveScene, width, height, scaling, crisp, playButtonOverlap },
  ref,
) {
  const game = useRef<Phaser.Game | null>(null!);
  const mountGenerationRef = useRef(0);
  const [_phaserReady, setPhaserReady] = useState(false);

  // --- Context-loss recovery ---
  const restartGame = () => {
    try {
      destroySingletonGame();
    } catch (err) {
      logger.warn('failed to destroy Phaser game before restart', err);
    }
    const result = getOrCreateSingletonGame('game-container', width, height);
    game.current = result.game;
    if (typeof ref === 'function') {
      ref({ game: game.current, scene: null, wasReused: false });
    } else if (ref) {
      ref.current = { game: game.current, scene: null, wasReused: false };
    }
    logger.info('Phaser game restarted after resume/context loss');
  };

  const wakeGame = () => {
    const currentGame = game.current;
    if (!currentGame) {
      restartGame();
      return;
    }
    const gl = (currentGame.renderer as Phaser.Renderer.WebGL.WebGLRenderer)?.gl;
    const contextLost = typeof gl?.isContextLost === 'function' ? gl.isContextLost() : false;
    if (contextLost) {
      restartGame();
      return;
    }
    currentGame.loop?.wake?.();
    const dpr = window.devicePixelRatio;
    const rendererWidth = crisp ? width * dpr : width;
    const rendererHeight = crisp ? height * dpr : height;
    currentGame.renderer?.resize?.(rendererWidth, rendererHeight);
    resumePhaserAudioContext(currentGame);
    NotifyPhaserEvents.ResizeWindow({ width, height, playButtonOverlap });
  };

  const sleepGame = () => {
    game.current?.loop?.sleep?.();
  };

  // --- Mount (once) ---
  // Create or reuse the singleton game on first mount only. Heavy cleanup
  // lives in the unmount-only effect below so that re-renders caused by
  // width/height changes don't tear down EventBus listeners.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    singleton.mountGeneration++;
    mountGenerationRef.current = singleton.mountGeneration;

    const result = getOrCreateSingletonGame('game-container', width, height, scaling, crisp);
    // Reset the flag AFTER getOrCreateSingletonGame has checked it.
    // On the next unmount, cleanup will set it back to true — unless
    // the mount-generation guard skips cleanup (route change case).
    singleton.cleanupRan = false;
    game.current = result.game;

    const activeScene = result.wasReused
      ? (game.current.scene.scenes.find((s: Phaser.Scene) => s.sys?.isActive()) ??
        game.current.scene.scenes[0] ??
        null)
      : null;

    if (typeof ref === 'function') {
      ref({ game: game.current, scene: activeScene, wasReused: result.wasReused });
    } else if (ref) {
      ref.current = { game: game.current, scene: activeScene, wasReused: result.wasReused };
    }
  }, []); // mount-only — size updates handled by Game's resize handlers

  // --- Unmount cleanup (once) ---
  // Separated from the mount effect so that re-renders (e.g. from
  // width/height prop changes) don't nuke EventBus listeners that
  // sibling components have registered.
  useEffect(() => {
    return () => {
      // When React remounts PhaserGame with a new key (e.g. transpose),
      // the new component's useLayoutEffect runs BEFORE this cleanup.
      // If a newer mount already took ownership, skip teardown — the new
      // mount has already woken the game and reattached the canvas, and
      // its loadScene() call will restart the scene (triggering the old
      // scene's shutdown → destroy, which cleans up its own listeners).
      const isStale = mountGenerationRef.current !== singleton.mountGeneration;
      if (isStale) {
        return;
      }
      singleton.cleanupRan = true;
      if (game.current) {
        // stopAllScenes triggers each scene's shutdown → destroy(), which
        // individually removes listeners that the scene registered.
        // We intentionally do NOT call EventBus.removeAllListeners() here
        // because parent components (e.g. MainAppPhaser) register React-side
        // listeners (SetAdaptiveTempoState, etc.) that must survive a
        // PhaserGame unmount/remount cycle (e.g. tier changes).
        stopAllScenes(game.current);
      }
      ClearQueueReact();
      ClearQueuePhaser();
      singleton.scaleCleanup();
      sleepSingletonGame();
      detachCanvas();
    };
  }, []);

  // --- Visibility / app-state ---
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        sleepGame();
      } else {
        wakeGame();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    let appStateSub: { remove?: () => void } | undefined;
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          wakeGame();
        } else {
          sleepGame();
        }
      })
        .then((handle) => {
          appStateSub = handle;
        })
        .catch(() => {});
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      appStateSub?.remove?.();
    };
  }, [height, width]);

  // --- Scene-ready bridge ---
  useEffect(() => {
    const onCurrentSceneReady = (scene_instance: Phaser.Scene) => {
      if (currentActiveScene && typeof currentActiveScene === 'function') {
        currentActiveScene(scene_instance);
      }
      if (typeof ref === 'function') {
        ref({ game: game.current, scene: scene_instance, wasReused: false });
      } else if (ref) {
        ref.current = { game: game.current, scene: scene_instance, wasReused: false };
      }
      setPhaserReady(true);
    };
    // Use ReactListenForPhaser with collectWaiting so we receive CurrentSceneReady
    // even if it was emitted (and queued) before this listener was registered.
    PhaserToReactListeners.CurrentSceneReady(onCurrentSceneReady, undefined, true);
    return () => {
      RemoveListener('CurrentSceneReady');
    };
  }, [currentActiveScene, ref]);

  return (
    <div
      id="game-container"
      style={{
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    ></div>
  );
});
